<template>
  <NuxtLayout>
    <ReservationPanel sticky @search="getNewData" />
  
    <section v-if="activeAccommodation" class="accommodationUnitList__ActiveAccommodation container--Medium" :key="activeAccommodation.id">
      <div class="accommodationUnitList__ImageWrapper">
        <Flare v-if="activeAccommodation.gallery?.length" :srcset="activeAccommodation.gallery[0]" size="both" width="420" height="300" crop="fill" :alt="activeAccommodation.gallery[0]" />
      </div>
      <div class="accommodationUnitList__ActiveAccommodationContent">
        <div>
          <div class="accommodationUnitList__LocationWrapper">
            <Icon name="location" />{{ activeAccommodation.place.name }} / {{ activeAccommodation.region.name }}
          </div>
          <h1 v-text="activeAccommodation.name" />
          <div v-if="activeAccommodation.category_rating" class="accommodationUnitList__StarsWrapper">
            <Icon name="star" v-for="star in activeAccommodation.category_rating" />
          </div>
        </div>
  
        <div v-if="activeAccommodation.sef" class="accommodationUnitList__HotelOverview">
          <Button type="nuxt-link" :to="activeAccommodation.sef" secondary medium>
            <template #prepend>
              <Icon name="arrow-left" />
            </template>
            {{ $t('hotelOverview') }}
          </Button>
        </div>
      </div>
    </section>

    <section v-if="responseError" class="apiFetchError container--Medium">
      <p v-text="$t(`fetchErrors.${ responseError }`)"></p>
    </section>
  
    <section v-if="false" class="accommodationUnitList__OffersWrapper">
      <div class="container--Medium">
         <FlickitySlider :options="flickityOptions" class="accommodationUnitList__Slider" :key="offersList.length">
          <div v-for="item in offersList" class="accommodationUnitList__OfferItem">
            <div>
              <div>
                <h2 v-text="item.title" />
                <p v-if="item.subtitle" v-text="item.subtitle" />
              </div>
              <div v-if="!item.package_external_id" class="accommodationUnitList__OfferRooms">
                <Icon name="check-circle" />
                {{ $t('allRoomsIncluded') }}
              </div>
              <nuxt-link v-else :to="item.sef" class="button__Text--Underline">
                {{ $t('seeOfferDetails') }}
                <Icon name="arrow-right" />
              </nuxt-link>
            </div>
            <div v-if="item.image_discount">
              <div class="accommodationUnitList__OfferDiscount">
                <Flare :srcset="item.image_discount" size="both" width="200" height="200" crop="fill" :alt="item.image_discount" />
              </div>
            </div>
          </div>
         </FlickitySlider>
      </div>
    </section>

    <div v-show="roomsList && Object.keys(roomsList)?.length" class="container--Medium">
      <div v-if="roomsList?.units?.length" class="accommodationUnitList__UnitsCounter">
        <p>{{ $t('roomsAvailable', roomsList.units.length) }}</p>
      </div>

      <client-only>
        <div v-if="booking" class="accommodationUnitList__LoyaltyWrapper" :class="{ 'isSwitchOff': !isLoyaltyPrices }">
          <template v-if="loyaltyToggleVisible">
            <CheckboxField v-model="isLoyaltyPrices" toggle :disabled="fetchingData" />
            <div class="accommodationUnisList__LoyaltyLabel">
              <h3 v-text="$t('accommodationUnits.loyaltyTitle')" />
            </div>
          </template>
          <div class="accommodationUnisList__LoyaltyText" :class="{ 'withoutMargin': !loyaltyToggleVisible }">
            <h4 v-text="$t('accommodationUnits.notAMember')" />
            <p v-text="$t('accommodationUnits.continue')" />

            <template v-if="status !== 'authenticated'">
              <h4 v-text="$t('accommodationUnits.alreadyAMember')" />
              <i18n-t tag="p" scope="global" keypath="accommodationUnits.memberText">
                <template #link>
                  <button class="button__Text--Underline" @click="open('login')">{{ $t('accommodationUnits.signIn') }}</button>
                </template>
              </i18n-t>
            </template>
          </div>
        </div>
      </client-only>
  
      <div :key="dataFetchCounter">
        <section v-if="roomsList?.units" class="accommodationUnitList__AvailableRoomsList">
          <RoomItem
            @open-modal="handleOpenModal"
            v-for="room in roomsList.units"
            :data="{ ...room, accommodationMealPlans }"
            :key="room.id"
            :fetching-data="fetchingData"
          />
        </section>
    
        <section v-if="roomsList?.unavailable_units?.length" class="accommodationUnitList__UnavailableRoomsList">
          <h3 v-text="$t('roomsNotAvailable')" />
          <RoomItem
            @open-modal="handleOpenModal"
            v-for="room in roomsList.unavailable_units"
            :data="room"
            :key="room.id"
          />
        </section>
  
        <section v-if="roomsList?.units_invalid_capacity?.length" class="accommodationUnitList__UnavailableRoomsList">
          <h3 v-text="$t('roomsOccupantsNotAvailable')" />
          <RoomItem
            @open-modal="handleOpenModal"
            v-for="room in roomsList.units_invalid_capacity"
            :data="room"
            :key="room.id"
            :disable-rates-button="true"
          />
        </section>
      </div>
    </div>
  </NuxtLayout>

  <Modal v-show="dialogs?.loyalty" v-model="dialogs.loyalty" class="pricelistSection__Modal">
    <div class="pricelistSection__ModalInner">
      <div class="pricelistSection__ModalTitles">
        <h3 v-text="$t('checkout.loyaltyModal.title')" />
        <h4 v-text="$t('checkout.loyaltyModal.subtitle')" />
      </div>

      <div class="pricelistSection__ModalBoxes">
        <div>
          <h5 v-text="$t('checkout.loyaltyModal.alreadyMember')" />

          <div class="customError__Wrapper">
            <div v-if="customError && Object.keys(customError).length" class="customError">
              <p v-text="$t(`loyaltyErrors.error${ customError.code }`)" />
            </div>
          </div>

          <form @submit.prevent="handleSubmit()" class="absoluteLabelsForm">
            <TextField v-model="form.email" :label="$t('forms.email')" type="email" />
            <TextField class="form__PasswordField form__PasswordFirstField" v-model="form.password" :label="$t('forms.password')" type="password">
              <template #append>
                <button type="button" @click="togglePasswordVisibility">
                  <Icon name="password" />
                  <Icon name="password-hide" />
                </button>
              </template>
            </TextField>
            
            <Button class="pricelistSection__SubmitButton" medium :disabled="!form.email || !form.password || loginDataFetch">{{ $t('checkout.loyaltyModal.login') }}</Button>
          </form>
        </div>
        
        <div>
          <div>
            <h5 v-text="$t('checkout.loyaltyModal.notMember')" />
            <p v-text="$t('checkout.loyaltyModal.notMemberText')" />
          </div>

          <Button medium secondary class="pricelistSection__NotMemberButton" @click="handleProceed">
            {{ $t('checkout.loyaltyModal.continueWithBooking') }}
            <template #append>
              <Icon name="arrow-right" />
            </template>
          </Button>
        </div>
      </div>

      <div class="pricelistSection__WithoutMembership">
        <p v-text="$t('checkout.loyaltyModal.bookWithoutLoyalty')" />
        <button type="button" class="button__Text--Underline" @click="handleReject">{{ $t('checkout.loyaltyModal.bookWithoutLoyaltyButton') }}</button>
      </div>
    </div>
  </Modal>

  <Modal v-show="dialogs.login" v-model="dialogs.login" class="accommodationUnitList__LoginModal">
    <h5 v-text="$t('login.login')" />

    <div class="customError__Wrapper">
      <div v-if="customError && Object.keys(customError).length" class="customError">
        <p v-text="$t(`loyaltyErrors.error${ customError.code }`)" />
      </div>
    </div>

    <form @submit.prevent="handleSubmit(true)" class="absoluteLabelsForm">
      <TextField v-model="form.email" :label="$t('forms.email')" type="email" />
      <TextField class="form__PasswordField loginModalPasswordField" v-model="form.password" :label="$t('forms.password')" type="password">
        <template #append>
          <button type="button" @click="togglePasswordVisibility($event, false, '.loginModalPasswordField')">
            <Icon name="password" />
            <Icon name="password-hide" />
          </button>
        </template>
      </TextField>
      
      <Button class="pricelistSection__SubmitButton" medium :disabled="!form.email || !form.password || loginDataFetch">{{ $t('checkout.loyaltyModal.login') }}</Button>
    </form>
  </Modal>

  <Loader :active="fetchingData"></Loader>
</template>

<script setup>
  import { add, format } from 'date-fns';
  const { link } = useSetup();
  const { query } = useRoute();
  const { signIn, status } = useAuth();
  const { accommodationsData } = useSetup();
  const { booking } = useBooking();
  const dialogs = reactive({
    loyalty: false,
    login: false,
  });
  const { open, close } = useModal(dialogs);

  const props = defineProps({
    page: {
      type: Object
    }
  });
  const responseError = ref(null);
  const roomsList = ref([]);
  const offersList = ref([]);
  const fetchingData = ref(false);
  const loginDataFetch = ref(false);
  const dataFetchCounter = ref(0);
  const activeAccommodation = ref(null);
  const customError = ref(null);
  const loyaltyToggleVisible = ref(true);
  const noLoyaltyPricesForPromoCode = ref(false);
  const isLoyaltyPrices = ref(JSON.parse(booking.value.loyalty_prices))
  const loginFormInitData = {
    email: '',
    password: ''
  };
  const form = ref(JSON.parse(JSON.stringify(loginFormInitData)));
  
  let requestBody = {};
  let _handleProceed = null;

  const flickityOptions = {
    pageDots: false,
    prevNextButtons: false,
    cellAlign: 'left',
    contain: true,
  };

  const accommodationMealPlans = computed(() => {
    if (activeAccommodation.value.accommodation_meal_plans?.length) {
      return activeAccommodation.value.accommodation_meal_plans.reduce((acc, nextItem) => {
        if (acc[nextItem.kind]) return acc;

        acc[nextItem.kind] = {...nextItem}
        return acc;
      }, {})
    }
  });

  // used for modal for loyalty login
  function handleOpenModal({ handleProceed: method }) {
    open('loyalty');
    _handleProceed = method;
  }

  function handleProceed() {
    _handleProceed();
  }

  // used for form in modal for loyalty login. It'll be visible when user checked loyalty prices and is not authenticated
  async function handleSubmit(closeModal = false) {
    customError.value = null;
    loginDataFetch.value = true;
    if (!form.value.email || !form.value.password) return;
    try {
      await signIn({ email: form.value.email, password: form.value.password }, { redirect: false }, {}, {
        'Application-Origin': 'SHAPE-WEB'
      });
      form.value = JSON.parse(JSON.stringify(loginFormInitData));

      if (closeModal) close('login');
      else handleProceed();
    } catch(error) {
      console.log(error.data)
      customError.value = {code: error.data.error_code } || {};
    }
    loginDataFetch.value = false;
  }

  // if user doesn't want to use loyalty prices, close the modal and fetch data without loyalty prices
  function handleReject() {
    close('loyalty');
    isLoyaltyPrices.value = false;
    booking.value.loyalty_prices = false;
  }

  async function getUnitsList() {
    return new Promise(async (resolve, reject) => {
      let response
      fetchingData.value = true;
      responseError.value = null;
      // every unit from the selected_units array is split by '/' because the value is in the format number of adults/number of children/children ages. For this request we need only roomId
      // selected_units are created in the PricelistSection component
      let _selectedUnits = booking.value.selected_units;
      if (typeof _selectedUnits === 'string') _selectedUnits = [_selectedUnits];
      requestBody = { ...booking.value, selected_units: _selectedUnits.map(i => i.split('/')[0]) };
      delete requestBody.rate_external_id;

      requestBody.accommodation = requestBody.accommodation?.id || requestBody.accommodation_id;
      if (requestBody.date_from) requestBody.date_from = format(new Date(requestBody.date_from), 'yyyy-MM-dd');
      if (requestBody.date_to) requestBody.date_to = format(new Date(requestBody.date_to), 'yyyy-MM-dd');
      if (requestBody?.loyalty_prices) requestBody.loyalty_prices = JSON.parse(requestBody.loyalty_prices);
      requestBody.selected_units = [];
      query.unit_id ? requestBody.unit_id = query.unit_id : delete requestBody.unit_id;
      
      if (requestBody?.children_age?.length) {
        requestBody.children_age = requestBody.children_age.filter(item => item !== null);
        requestBody.children = requestBody.children_age.length;
        booking.value.children_age = JSON.parse(JSON.stringify(requestBody.children_age))
        booking.value.children = requestBody.children;
      }
      
      try {
        response = await useClientFetch('search/unit/', {
          method: 'POST',
          body: requestBody,
        })

        if (requestBody.promo_code) {
          if (requestBody.loyalty_prices) {
            if (!response.units.length) {
              noLoyaltyPricesForPromoCode.value = true;
              loyaltyToggleVisible.value = false;
              if (import.meta.client) isLoyaltyPrices.value = false;
            } else {
              loyaltyToggleVisible.value = true;
            }
          } else {
            if (noLoyaltyPricesForPromoCode.value) {
              noLoyaltyPricesForPromoCode.value = false;
              loyaltyToggleVisible.value = false;
            } else {
              loyaltyToggleVisible.value = true;
            }
          }
        } else {
          loyaltyToggleVisible.value = true;
        }

        resolve()
      } catch(error) {
        const errorResponse = await handleApiErrorsRedirects(error, link('Error'));
        if (['booking_new_unit_not_available', 'invalid_promo_code'].includes(errorResponse)) {
          responseError.value = errorResponse;
        }
        reject(error)
      } finally {
        response ||= []
        if (response) {
          roomsList.value = response;
          activeAccommodation.value = accommodationsData?.value[requestBody?.accommodation];
          if(import.meta.client) getThemeStyles(activeAccommodation.value);
        }
        fetchingData.value = false;
        dataFetchCounter.value++;
      }
    })
  }

  async function getOffersList() {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await useClientFetch('offer/', {
          params: {
            accommodation: requestBody.accommodation,
          }
        })
        
        if (response) {
          offersList.value = response.results;
        }
        resolve()
      } catch(e) {
        console.log(e);
        offersList.value = [];
        reject(e)
      }
    })
  }

  async function getNewData() {
    return new Promise(async (resolve, reject) => {
      try {
        await getUnitsList();
        if (import.meta.client) {
          setTimeout(() => {
            scrollRoomIntoView();
          }, 800);
        }
        resolve()
      } catch(e) {
        reject(e)
      }
    })
  }

  function scrollRoomIntoView() {
    if (query.room_id) {
      const element = document.querySelectorAll(`#${query.room_id}`)[0];
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  watch(isLoyaltyPrices, async (value) => {
    booking.value.loyalty_prices = value;
    await useRouter().replace({ query: { ...useRoute().query, loyalty_prices: value } });
    await getUnitsList();
  })

  onMounted(async () => {
    let activeAccommodationId = booking.value.accommodation.id || booking.value.accommodation_id || {};
    if (activeAccommodationId) activeAccommodation.value = accommodationsData.value[activeAccommodationId];
    getThemeStyles(activeAccommodation.value);

    setTimeout(() => {
      if (query.unit_id) {
        const element = document.querySelector('.accommodationUnitList__AvailableRoomsList');
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }

      if (!loyaltyToggleVisible.value) isLoyaltyPrices.value = false;
    }, 10);
  });

  onBeforeUnmount(() => {
    removeThemeStyles();
  });

  provide('clearUnitsList', true);
  
  try {
    if(Object.keys(useRouter().currentRoute.value.query).length === 0) await navigateTo(link('Home'))
    else await getNewData();
  } catch(e) {
    console.log(e);
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_accommodationUnitList.scss');
</style>